import React, { Component } from "react";
import PropTypes from "prop-types";

import LineSeries from "./LineSeries";

const normalizeStroke = (stroke, fallback) => ({
  color: stroke?.color || fallback.color,
  dash: stroke?.dash || fallback.dash,
  width: stroke?.width ?? fallback.width,
  opacity: stroke?.opacity ?? fallback.opacity,
});

// Rainbow colors from red to magenta
const FALLBACK_SMA1 = { color: "#FF0000", dash: "Solid", width: 1, opacity: 1 }; // Red
const FALLBACK_SMA2 = { color: "#FF7F00", dash: "Solid", width: 1, opacity: 1 }; // Orange
const FALLBACK_SMA3 = { color: "#FFFF00", dash: "Solid", width: 1, opacity: 1 }; // Yellow
const FALLBACK_SMA4 = { color: "#7FFF00", dash: "Solid", width: 1, opacity: 1 }; // Yellow-Green
const FALLBACK_SMA5 = { color: "#00FF7F", dash: "Solid", width: 1, opacity: 1 }; // Green-Cyan
const FALLBACK_SMA6 = { color: "#00FFFF", dash: "Solid", width: 1, opacity: 1 }; // Cyan
const FALLBACK_SMA7 = { color: "#007FFF", dash: "Solid", width: 1, opacity: 1 }; // Cyan-Blue
const FALLBACK_SMA8 = { color: "#0000FF", dash: "Solid", width: 1, opacity: 1 }; // Blue
const FALLBACK_SMA9 = { color: "#7F00FF", dash: "Solid", width: 1, opacity: 1 }; // Purple
const FALLBACK_SMA10 = {
  color: "#FF00FF",
  dash: "Solid",
  width: 1,
  opacity: 1,
}; // Magenta

class RainbowMASeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorSMA1 = this.yAccessorSMA1.bind(this);
    this.yAccessorSMA2 = this.yAccessorSMA2.bind(this);
    this.yAccessorSMA3 = this.yAccessorSMA3.bind(this);
    this.yAccessorSMA4 = this.yAccessorSMA4.bind(this);
    this.yAccessorSMA5 = this.yAccessorSMA5.bind(this);
    this.yAccessorSMA6 = this.yAccessorSMA6.bind(this);
    this.yAccessorSMA7 = this.yAccessorSMA7.bind(this);
    this.yAccessorSMA8 = this.yAccessorSMA8.bind(this);
    this.yAccessorSMA9 = this.yAccessorSMA9.bind(this);
    this.yAccessorSMA10 = this.yAccessorSMA10.bind(this);
  }

  static yExtents(datum, index, allData, indicator) {
    const dataArr = Array.isArray(allData)
      ? allData
      : Array.isArray(datum)
        ? datum
        : [datum];

    if (!indicator || !indicator.renderKey) {
      return undefined;
    }

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    const updateExtents = (value) => {
      if (!Number.isFinite(value)) return;
      if (value < min) min = value;
      if (value > max) max = value;
    };

    dataArr.forEach((item) => {
      const node = item && item[indicator.renderKey];
      if (!node || typeof node !== "object") return;

      updateExtents(node.sma1);
      updateExtents(node.sma2);
      updateExtents(node.sma3);
      updateExtents(node.sma4);
      updateExtents(node.sma5);
      updateExtents(node.sma6);
      updateExtents(node.sma7);
      updateExtents(node.sma8);
      updateExtents(node.sma9);
      updateExtents(node.sma10);
    });

    // If no valid data, return undefined to use price extents
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return undefined;
    }

    // Add 2% padding
    const range = max - min;
    const padding = range * 0.02;

    return [min - padding, max + padding];
  }

  valueFromDatum(d, key) {
    if (!d) return undefined;
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    const raw = node ? node[key] : undefined;
    return Number.isFinite(raw) ? raw : undefined;
  }

  yAccessorSMA1(d) {
    return this.valueFromDatum(d, "sma1");
  }

  yAccessorSMA2(d) {
    return this.valueFromDatum(d, "sma2");
  }

  yAccessorSMA3(d) {
    return this.valueFromDatum(d, "sma3");
  }

  yAccessorSMA4(d) {
    return this.valueFromDatum(d, "sma4");
  }

  yAccessorSMA5(d) {
    return this.valueFromDatum(d, "sma5");
  }

  yAccessorSMA6(d) {
    return this.valueFromDatum(d, "sma6");
  }

  yAccessorSMA7(d) {
    return this.valueFromDatum(d, "sma7");
  }

  yAccessorSMA8(d) {
    return this.valueFromDatum(d, "sma8");
  }

  yAccessorSMA9(d) {
    return this.valueFromDatum(d, "sma9");
  }

  yAccessorSMA10(d) {
    return this.valueFromDatum(d, "sma10");
  }

  render() {
    const { className, onDoubleClick, onContextMenu, clip } = this.props;
    const {
      strokeSMA1,
      strokeSMA2,
      strokeSMA3,
      strokeSMA4,
      strokeSMA5,
      strokeSMA6,
      strokeSMA7,
      strokeSMA8,
      strokeSMA9,
      strokeSMA10,
    } = this.props;

    const sma1Stroke = normalizeStroke(strokeSMA1, FALLBACK_SMA1);
    const sma2Stroke = normalizeStroke(strokeSMA2, FALLBACK_SMA2);
    const sma3Stroke = normalizeStroke(strokeSMA3, FALLBACK_SMA3);
    const sma4Stroke = normalizeStroke(strokeSMA4, FALLBACK_SMA4);
    const sma5Stroke = normalizeStroke(strokeSMA5, FALLBACK_SMA5);
    const sma6Stroke = normalizeStroke(strokeSMA6, FALLBACK_SMA6);
    const sma7Stroke = normalizeStroke(strokeSMA7, FALLBACK_SMA7);
    const sma8Stroke = normalizeStroke(strokeSMA8, FALLBACK_SMA8);
    const sma9Stroke = normalizeStroke(strokeSMA9, FALLBACK_SMA9);
    const sma10Stroke = normalizeStroke(strokeSMA10, FALLBACK_SMA10);

    return (
      <g className={className}>
        <LineSeries
          yAccessor={this.yAccessorSMA1}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma1Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA2}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma2Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA3}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma3Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA4}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma4Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA5}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma5Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA6}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma6Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA7}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma7Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA8}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma8Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA9}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma9Stroke}
          clip={clip}
        />
        <LineSeries
          yAccessor={this.yAccessorSMA10}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={sma10Stroke}
          clip={clip}
        />
      </g>
    );
  }
}

RainbowMASeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  clip: PropTypes.bool.isRequired,
  strokeSMA1: PropTypes.object.isRequired,
  strokeSMA2: PropTypes.object.isRequired,
  strokeSMA3: PropTypes.object.isRequired,
  strokeSMA4: PropTypes.object.isRequired,
  strokeSMA5: PropTypes.object.isRequired,
  strokeSMA6: PropTypes.object.isRequired,
  strokeSMA7: PropTypes.object.isRequired,
  strokeSMA8: PropTypes.object.isRequired,
  strokeSMA9: PropTypes.object.isRequired,
  strokeSMA10: PropTypes.object.isRequired,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

RainbowMASeries.defaultProps = {
  className: "react-stockcharts-rainbowma-series",
  clip: true,
};

export default RainbowMASeries;
